import { TIMELINE_EVENT_SLIDE_YEAR_CHANGE } from './timeline-constants';

export class TimelineComponentHelper {
    private shadowRoot: ShadowRoot;

    constructor(private componentElm: HTMLElement) {
        this.shadowRoot = componentElm.shadowRoot as ShadowRoot;
    }

    setupAnimatedTextElm(
        elmSelector: string,
        contentAttribute: string | null,
        isHtml: boolean,
        delayAttribute: string,
        durationAttribute: string
    ) {
        const elm = this.shadowRoot.querySelector(elmSelector) as HTMLElement;
        if (!elm) return;
        const delay = this.componentElm.getAttribute(delayAttribute) || '0';
        const duration = this.componentElm.getAttribute(durationAttribute) || '1000';
        if (isHtml) {
            this.setHtmlFromAttribute(elmSelector, contentAttribute);
        } else {
            this.setContentFromAttribute(elmSelector, contentAttribute);
        }
        this.setAnimationProps(elm, delay, duration);
    }

    setAnimationProps(elm: HTMLElement, delay: string, duration: string) {
        elm.style.setProperty('--animation-delay', delay + 'ms');
        elm.style.setProperty('--animation-duration', duration + 'ms');
    }

    setHtmlFromAttribute(elmSelector: string, contentAttribute: string | null) {
        const elm = this.shadowRoot.querySelector(elmSelector) as HTMLElement;
        if (!elm) return;
        if (contentAttribute) {
            const content = this.componentElm.getAttribute(contentAttribute);
            elm.innerHTML = content || '';
        }
    }

    setContentFromAttribute(elmSelector: string, contentAttribute: string | null) {
        const elm = this.shadowRoot.querySelector(elmSelector) as HTMLElement;
        if (!elm) return;
        if (contentAttribute) {
            const content = this.componentElm.getAttribute(contentAttribute);
            elm.innerText = content || '';
        }
    }

    setupYearChangeEventDispatcher(year: string | null) {
        if (!year) {
            throw new Error('Year is required to setup year change event dispatcher.');
        }

        const fireYearChangedEvent = (year: string) => {
            const event = new CustomEvent(TIMELINE_EVENT_SLIDE_YEAR_CHANGE, { detail: year, bubbles: true });
            this.componentElm.dispatchEvent(event);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        fireYearChangedEvent(year);
                    }
                });
            },
            { rootMargin: '-40% 0px' }
        );
        observer.observe(this.componentElm);
    }

    /**
     * Set up enter/exit animations for elements. They will require to have the animation css custom properties --animation-delay and --animation-duration set.
     * See setAnimationProps() for setting these properties (or higher level functions like setupAnimatedTextElm()).
     * @param elms Elements to animate.
     * @param animationClass The class to add to the element when it enters the viewport.
     * @param enterIntersectionObserverOptions Options to pass to the enter animation IntersectionObserver.
     * @param exitIntersectionObserverOptions Options to pass to the exit animation IntersectionObserver.
     * @returns A function that unobserves the elements.
     */
    enableEnterExitAnimations(
        elms: HTMLElement[],
        animationClass: string,
        enterIntersectionObserverOptions: IntersectionObserverInit = {},
        exitIntersectionObserverOptions: IntersectionObserverInit = {}
    ) {
        if (!elms || elms.length === 0) return () => {};

        const enterAnimationObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const elm = entry.target as HTMLElement;
                        elm.classList.add(animationClass);
                    }
                });
            },
            { rootMargin: '-200px 0px', ...enterIntersectionObserverOptions }
        );

        // The exit observer is separate to allow for a different rootMargin.
        // If the root margin was also a negative value, elements would pop out of existence on screen.
        const exitAnimationObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        const elm = entry.target as HTMLElement;
                        elm.classList.remove(animationClass);
                    }
                });
            },
            { rootMargin: '0px', ...exitIntersectionObserverOptions }
        );

        elms.forEach((elm) => {
            enterAnimationObserver.observe(elm);
            exitAnimationObserver.observe(elm);
        });

        // Return function that unobserves the element.
        return () => {
            enterAnimationObserver.disconnect();
            exitAnimationObserver.disconnect();
        };
    }
}
